import type { SessionResponse } from '@fuel-system/contracts';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiRequest, ApiRequestError } from '@/lib/api-client';

/** This app is the `pda` Unified Access platform (FUEL_PDA). */
const APP_SLUG = 'pda';

/** The Unified Access web app this platform trusts. */
export function uaPortalUrl(): string {
  return (import.meta.env['VITE_UA_PORTAL_URL'] as string | undefined) ?? 'http://localhost:5200';
}

/**
 * fuel-pda.md section 2.1 and unified-access.md section 3.2: the PDA asks
 * Unified Access for single-platform sign-in (`lock=1`), so the employee only
 * ever sees the sign-in pages and comes straight back here, never the portal.
 * Built from the deployed web app, so the installed APK needs no new build.
 */
export function uaSignInUrl(page: 'login' | 'logout', returnTo: string): string {
  return `${uaPortalUrl()}/${page}?lock=1&returnTo=${encodeURIComponent(returnTo)}`;
}

/**
 * unified-access.md section 3, step 5 and ui-contract.md section 6: sends the
 * browser to Unified Access with the current absolute page as `returnTo`, so
 * the employee lands back on the same page after signing in again.
 */
export function redirectToSignIn(): void {
  globalThis.location.href = uaSignInUrl('login', globalThis.location.href);
}

function homeUrl(): string {
  return `${globalThis.location.origin}/`;
}

/** Opening the launch page without a code asks Unified Access for one. */
export function redirectToSignInHome(): void {
  globalThis.location.href = uaSignInUrl('login', homeUrl());
}

/**
 * A launch code this API refused (for example NO_PLATFORM_ACCESS: no active
 * Fuel user in that branch) ends the Unified Access session too, with the
 * reason shown on the login form. Returning to the login would only issue
 * another code for the same refusal, in a loop (unified-access.md 3.2).
 */
export function signOutAfterRefusedLaunch(error: unknown): void {
  const reason =
    error instanceof ApiRequestError && error.code === 'NO_PLATFORM_ACCESS'
      ? 'NO_PLATFORM_ACCESS'
      : 'LAUNCH_FAILED';
  globalThis.location.href = `${uaSignInUrl('logout', homeUrl())}&reason=${reason}`;
}

/**
 * What GET /auth/pda/me returns: the shared session plus, when the API
 * exposes them, the business date and the frozen flag (fuel-pda.md 4.1).
 */
export type PdaSession = SessionResponse & { businessDate?: string; frozen?: boolean };

export const SESSION_QUERY_KEY = ['session'] as const;

/**
 * Cookie session, so there is no client-held token to branch on: GET
 * /auth/pda/me is the source of truth. A 401 means "no session"; every
 * other failure is a real error the caller should show.
 */
export function useSession() {
  return useQuery({
    queryKey: SESSION_QUERY_KEY,
    queryFn: () => apiRequest<PdaSession>(`/auth/${APP_SLUG}/me`),
    retry: false,
    staleTime: 60_000,
  });
}

export function isUnauthenticated(error: unknown): boolean {
  return error instanceof ApiRequestError && error.status === 401;
}

/**
 * Redeems a launch code from Unified Access, opening the app's own session
 * cookie server-side.
 */
export function useLaunch() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (code: string) =>
      apiRequest<SessionResponse>(`/auth/${APP_SLUG}/launch`, { method: 'POST', body: { code } }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: SESSION_QUERY_KEY });
    },
  });
}

/**
 * Signing out of this app clears its own session cookie, then sends the
 * browser to the Unified Access sign-out (unified-access.md section 3, step
 * 6), which ends the central session and the PDA session launched from it
 * and shows the login form. The lock and the PDA home as `returnTo` bring
 * the next sign-in straight back here (fuel-pda.md section 2.1).
 */
export function useLogout() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => apiRequest<void>(`/auth/${APP_SLUG}/logout`, { method: 'POST' }),
    onSettled: () => {
      queryClient.clear();
      globalThis.location.href = uaSignInUrl('logout', homeUrl());
    },
  });
}
